// Payout Forecast — upcoming N-day disbursement calendar for cashflow
// planning. "How much cash do I need in the next 30/60/90 days, when, for
// whom?" Horizon picker = 30 / 60 / 90 days.

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { current_language } from '../i18n/index.js';
import { format_inr, short_month_label } from '../lib/format.js';
import { friendly_error } from '../lib/friendly-error.js';
import { orpc } from '../lib/orpc.js';
import { Icon } from '../components/Icon.js';

interface ForecastData {
  horizon_days: number;
  from_date: string;
  to_date: string;
  total_paise: number;
  total_count: number;
  by_month: Array<{ month: string; count: number; total_paise: number }>;
  rows: Array<{
    chit_id: string;
    chit_name_ta: string;
    chit_name_en: string;
    cycle_index: number;
    cycle_date: string;
    receive_amount_paise: number;
    recipient_subscriber_id: string | null;
    recipient_name_en: string | null;
    recipient_name_ta: string | null;
    recipient_phone: string | null;
    days_from_now: number;
    status: 'today' | 'this_week' | 'this_month' | 'future';
  }>;
}

const STATUS_LABEL_TA: Record<string, string> = {
  today: 'இன்று',
  this_week: 'வாரம்',
  this_month: 'மாதம்',
  future: 'எதிர்காலம்',
};
const STATUS_LABEL_EN: Record<string, string> = {
  today: 'Today',
  this_week: 'This week',
  this_month: 'This month',
  future: 'Future',
};

export function ReportsPayoutForecastPage(): ReactElement {
  const lang = current_language();
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const [horizon_days, set_horizon_days] = useState(60);

  const q = useQuery({
    queryKey: ['report-payout-forecast', horizon_days],
    queryFn: () => orpc.reports.payout_forecast({ horizon_days }) as Promise<ForecastData>,
  });
  const data = q.data;

  const export_csv = (): void => {
    if (!data) return;
    const lines = [
      'cycle_date,days_from_now,chit_id,chit_name,cycle_index,recipient_subscriber_id,recipient_name,recipient_phone,amount_rupees,status',
      ...data.rows.map((r) =>
        [
          r.cycle_date,
          r.days_from_now,
          r.chit_id,
          (r.chit_name_en || r.chit_name_ta).replace(/,/g, ' '),
          r.cycle_index,
          r.recipient_subscriber_id ?? '',
          ((r.recipient_name_en || r.recipient_name_ta) ?? '').replace(/,/g, ' '),
          r.recipient_phone ?? '',
          (r.receive_amount_paise / 100).toFixed(2),
          r.status,
        ].join(','),
      ),
    ];
    const blob = new Blob(['﻿', lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payout-forecast-${data.from_date}-${data.to_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [preview, set_preview] = useState<string | null>(null);
  const pdf_m = useMutation({
    mutationFn: () => orpc.reports.payout_forecast_pdf({ horizon_days }),
    onSuccess: (out) => {
      if (out.preview_html) set_preview(out.preview_html);
      else window.open(out.pdf_url, '_blank');
    },
  });

  return (
    <div className="page full">
      <div className="page-head">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Link to="/reports" style={{ color: 'var(--ink-3)', textDecoration: 'none', fontSize: 12 }}>
              ← {t('அறிக்கைகள்', 'Reports')}
            </Link>
          </div>
          <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('பணம் வழங்கல் கணிப்பு', 'Payout Forecast')}</div>
          <div className="h-sub">{data ? `${data.from_date} → ${data.to_date}` : '—'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('காலம்', 'Horizon')}</span>
          {[30, 60, 90].map((d) => (
            <button key={d} type="button" className={'btn sm' + (horizon_days === d ? ' primary' : '')} onClick={() => set_horizon_days(d)}>
              {d}d
            </button>
          ))}
          <button type="button" className="btn" onClick={export_csv} disabled={!data}>
            <Icon.download /> CSV
          </button>
          <button type="button" className="btn primary" onClick={() => pdf_m.mutate()} disabled={pdf_m.isPending}>
            <Icon.download /> {pdf_m.isPending ? '…' : 'PDF'}
          </button>
        </div>
      </div>

      <div className="kpis" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <div className="kpi"><div className="kpi-label">{t('மொத்த சுழற்சிகள்', 'Total cycles')}</div><div className="kpi-value">{data?.total_count ?? '—'}</div><div className="kpi-sub">{data ? `${horizon_days} ${t('நாள்', 'days')}` : ''}</div></div>
        <div className="kpi primary"><div className="kpi-label">{t('தேவையான பணம்', 'Cash required')}</div><div className="kpi-value">{data ? format_inr(data.total_paise) : '—'}</div></div>
      </div>

      {q.isLoading && <div style={{ padding: 20, color: 'var(--ink-3)' }}>{t('ஏற்றுகிறது…', 'Loading…')}</div>}
      {q.error && <div style={{ padding: 20, color: 'var(--bad)' }}>{friendly_error(q.error)}</div>}

      {data && data.by_month.length > 0 && (
        <div className="card">
          <div className="card-head"><div className="card-title">{t('மாதம் வாரியாக', 'By month')}</div></div>
          <div style={{ padding: 14, display: 'flex', gap: 10, overflowX: 'auto' }}>
            {data.by_month.map((m) => (
              <div key={m.month} className="kpi" style={{ flex: '0 0 auto', minWidth: 140 }}>
                <div className="kpi-label">{short_month_label(m.month, lang)} {m.month.slice(0, 4)}</div>
                <div className="kpi-value">{format_inr(m.total_paise)}</div>
                <div className="kpi-sub">{m.count} {t('சுழற்சிகள்', 'cycles')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data && data.rows.length === 0 && (
        <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--ink-3)' }}>
          {t('எதிர்கால சுழற்சிகள் இல்லை', 'No upcoming payouts in this window')}
        </div>
      )}

      {data && data.rows.length > 0 && (
        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: 'var(--bg-elev)' }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('தேதி', 'Date')}</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>+d</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('நிலை', 'Status')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('சீட்டு', 'Chit')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('பெறுநர்', 'Recipient')}</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>{t('பணம்', 'Amount')}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => {
                  const status_bg = r.status === 'today' ? 'var(--bad-soft)' : r.status === 'this_week' ? 'var(--warn-soft)' : undefined;
                  const status_label = lang === 'ta' ? STATUS_LABEL_TA[r.status] : STATUS_LABEL_EN[r.status];
                  return (
                    <tr key={i} style={{ borderTop: '1px solid var(--line)', background: status_bg }}>
                      <td style={{ padding: '6px 10px' }} className="mono">{r.cycle_date}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right' }} className="mono">{r.days_from_now}</td>
                      <td style={{ padding: '6px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{status_label}</td>
                      <td style={{ padding: '6px 10px' }} className={lang === 'ta' ? 'tamil' : ''}>
                        <Link to={`/chits/${r.chit_id}`} style={{ color: 'var(--ink)', textDecoration: 'none' }}>
                          {(lang === 'ta' ? r.chit_name_ta : r.chit_name_en) || '—'}
                        </Link>
                        <span style={{ color: 'var(--ink-3)', fontSize: 11, marginLeft: 4 }}>#{r.cycle_index}</span>
                      </td>
                      <td style={{ padding: '6px 10px' }} className={lang === 'ta' ? 'tamil' : ''}>
                        {r.recipient_subscriber_id ? (
                          <Link to={`/subscribers/${r.recipient_subscriber_id}`} style={{ color: 'var(--ink)', textDecoration: 'none' }}>
                            {(lang === 'ta' ? r.recipient_name_ta : r.recipient_name_en) || '—'}
                          </Link>
                        ) : <span style={{ color: 'var(--ink-3)' }}>{t('ஒதுக்கப்படவில்லை', 'unassigned')}</span>}
                        {r.recipient_phone && <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{r.recipient_phone}</div>}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }} className="mono">{format_inr(r.receive_amount_paise)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--line)', background: 'var(--bg-elev)' }}>
                  <td colSpan={5} style={{ padding: '8px 10px', fontWeight: 600 }}>{t('மொத்தம்', 'Total')}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }} className="mono">{format_inr(data.total_paise)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {pdf_m.error && <div style={{ color: 'var(--bad)', padding: 10 }}>{friendly_error(pdf_m.error)}</div>}
      {preview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,24,20,0.45)', zIndex: 10000, padding: 16, overflow: 'auto' }}>
          <div style={{ maxWidth: 1000, margin: 'auto', background: 'white', borderRadius: 8 }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>PDF preview (DRY_RUN)</div>
              <button type="button" className="btn sm" onClick={() => set_preview(null)}>Close</button>
            </div>
            <iframe title="pf-preview" srcDoc={preview} style={{ width: '100%', height: '80vh', border: 0 }} />
          </div>
        </div>
      )}
    </div>
  );
}
